// utils/stats.js
import { auth, db } from './firebaseConfig';
import { 
  doc, 
  collection, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc, 
  arrayUnion, 
  Timestamp 
} from 'firebase/firestore';

/**
 * Stellt Statistikfunktionen für die Anwendung bereit
 * mit Unterstützung für lokale (localStorage) und Cloud-Speicherung (Firebase)
 */
export const statsUtils = {
  /**
   * Prüft, ob der Benutzer angemeldet ist
   * @returns {boolean} - true, wenn der Benutzer angemeldet ist
   */
  isUserLoggedIn: function() {
    return auth.currentUser !== null;
  },

  /**
   * Erzeugt ein einheitliches Ergebnisdatenobjekt
   * @param {Object} result - Ergebnis der Übung
   * @returns {Object} - Formatiertes Ergebnisobjekt
   */
  createResultObject: function(result) {
    return {
      ...result,
      timestamp: Date.now(),
      date: new Date().toLocaleDateString(),
      points: this.calculatePoints(result.cpm, result.errorRate)
    };
  },

  /**
   * Speichert ein Übungsergebnis in Firebase oder LocalStorage
   * @param {Object} result - Ergebnis der Übung
   * @param {string} result.title - Titel der Übung
   * @param {string} result.description - Beschreibung der Übung
   * @param {number} result.cpm - Zeichen pro Minute
   * @param {number} result.errorRate - Fehlerquote in Prozent
   * @param {number} result.duration - Dauer der Übung in Sekunden
   * @param {number} result.errors - Anzahl der Fehler
   * @param {Object} result.mistakes - Fehler nach Tasten
   */
  saveExerciseResult: async function(result) {
    // Prüfen ob Browser-Umgebung
    if (typeof window === 'undefined') return;
    
    const newResult = this.createResultObject(result);
    
    // Prüfen, ob der Benutzer angemeldet ist
    if (this.isUserLoggedIn()) {
      try {
        const userId = auth.currentUser.uid;
        console.log("Speichere Ergebnis in Firebase für Benutzer:", userId);
        
        // Speichern in Firebase
        const userStatsRef = doc(db, "users", userId);
        
        // Prüfen, ob User-Dokument existiert
        const userDoc = await getDoc(userStatsRef);
        
        if (userDoc.exists()) {
          // Aktualisieren des bestehenden Dokuments
          await updateDoc(userStatsRef, {
            exerciseResults: arrayUnion(newResult),
            lastUpdated: Timestamp.now()
          });
        } else {
          // Neues Dokument erstellen
          await setDoc(userStatsRef, {
            exerciseResults: [newResult],
            lastUpdated: Timestamp.now()
          });
        }
        
        // Fehlerstatistiken aktualisieren
        await this.updateFirebaseMistakesStats(result.mistakes);
        
        console.log("Übungsergebnis in Firebase gespeichert");
      } catch (error) {
        console.error("Fehler beim Speichern in Firebase:", error);
        // Fallback zu LocalStorage bei Fehler
        this.saveToLocalStorage(newResult, result.mistakes);
      }
    } else {
      // Speichern im LocalStorage, wenn nicht angemeldet
      this.saveToLocalStorage(newResult, result.mistakes);
    }
    
    return newResult;
  },
  
  /**
   * Speichert Daten in LocalStorage
   * @param {Object} newResult - Neues Ergebnisobjekt
   * @param {Object} mistakes - Fehler nach Tasten
   */
  saveToLocalStorage: function(newResult, mistakes) {
    // Vorhandene Ergebnisse abrufen
    const storedResults = localStorage.getItem('exercise_results');
    let results = storedResults ? JSON.parse(storedResults) : [];
    
    // Hinzufügen und speichern
    results.push(newResult);
    localStorage.setItem('exercise_results', JSON.stringify(results));
    
    // Auch Fehler speichern
    this.updateLocalMistakesStats(mistakes);
    
    console.log("Übungsergebnis in LocalStorage gespeichert:", newResult);
  },
  
  /**
   * Aktualisiert Fehlerstatistiken in Firebase
   * @param {Object} mistakes - Fehler nach Tasten
   */
  updateFirebaseMistakesStats: async function(mistakes) {
    if (!mistakes || !this.isUserLoggedIn()) return;
    
    try {
      const userId = auth.currentUser.uid;
      const userStatsRef = doc(db, "users", userId);
      
      // Aktuelle Fehlerstatistiken abrufen
      const userDoc = await getDoc(userStatsRef);
      let mistakesData = {};
      
      if (userDoc.exists() && userDoc.data().mistakesStats) {
        mistakesData = userDoc.data().mistakesStats;
      }
      
      // Neue Fehler zum bestehenden Objekt hinzufügen
      Object.keys(mistakes).forEach(key => {
        if (!mistakesData[key]) {
          mistakesData[key] = {
            count: 0,
            wrongKeys: {}
          };
        }
        
        mistakesData[key].count += mistakes[key].count || 1;
        
        // Falsch gedrückte Tasten aktualisieren
        if (mistakes[key].wrongKeys) {
          Object.keys(mistakes[key].wrongKeys).forEach(wrongKey => {
            if (!mistakesData[key].wrongKeys[wrongKey]) {
              mistakesData[key].wrongKeys[wrongKey] = 0;
            }
            mistakesData[key].wrongKeys[wrongKey] += mistakes[key].wrongKeys[wrongKey];
          });
        }
      });
      
      // Aktualisierte Daten speichern
      await updateDoc(userStatsRef, {
        mistakesStats: mistakesData,
        lastUpdated: Timestamp.now()
      });
      
      console.log("Fehlerstatistiken in Firebase aktualisiert");
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Fehlerstatistiken in Firebase:", error);
      // Fallback zu LocalStorage bei Fehler
      this.updateLocalMistakesStats(mistakes);
    }
  },
  
  /**
   * Aktualisiert Fehlerstatistiken im LocalStorage
   * @param {Object} mistakes - Fehler nach Tasten
   */
  updateLocalMistakesStats: function(mistakes) {
    if (!mistakes || typeof window === 'undefined') return;
    
    // Vorhandene Fehlerdaten abrufen
    const storedMistakes = localStorage.getItem('key_mistakes');
    let mistakesData = storedMistakes ? JSON.parse(storedMistakes) : {};
    
    // Neue Fehler zu bestehenden hinzufügen
    Object.keys(mistakes).forEach(key => {
      if (!mistakesData[key]) {
        mistakesData[key] = {
          count: 0,
          wrongKeys: {}
        };
      }
      
      mistakesData[key].count += mistakes[key].count || 1;
      
      // Falsch gedrückte Tasten aktualisieren
      if (mistakes[key].wrongKeys) {
        Object.keys(mistakes[key].wrongKeys).forEach(wrongKey => {
          if (!mistakesData[key].wrongKeys[wrongKey]) {
            mistakesData[key].wrongKeys[wrongKey] = 0;
          }
          mistakesData[key].wrongKeys[wrongKey] += mistakes[key].wrongKeys[wrongKey];
        });
      }
    });
    
    // Aktualisierte Daten speichern
    localStorage.setItem('key_mistakes', JSON.stringify(mistakesData));
  },
  
  /**
   * Berechnet Punkte basierend auf Geschwindigkeit und Fehlerquote
   * @param {number} cpm - Zeichen pro Minute
   * @param {number} errorRate - Fehlerquote in Prozent
   * @returns {number} - Berechnete Punkte
   */
  calculatePoints: function(cpm, errorRate) {
    // Einfache Formel: (CPM - (Fehlerquote * 10)) / 10 * 15
    // Eine Fehlerquote von 5% reduziert die Punkte um 50
    return Math.max(0, Math.round((cpm - (errorRate * 10)) / 10 * 15));
  },
  
  /**
   * Ruft alle gespeicherten Übungsergebnisse ab (zuerst aus Firebase, dann Fallback auf LocalStorage)
   * @returns {Promise<Array>} - Liste der Übungsergebnisse oder leeres Array
   */
  getExerciseResults: async function() {
    // Prüfen ob Browser-Umgebung
    if (typeof window === 'undefined') return [];
    
    // Versuchen, Daten aus Firebase zu laden, wenn Benutzer angemeldet ist
    if (this.isUserLoggedIn()) {
      try {
        const userId = auth.currentUser.uid;
        const userStatsRef = doc(db, "users", userId);
        const userDoc = await getDoc(userStatsRef);
        
        if (userDoc.exists() && userDoc.data().exerciseResults) {
          console.log("Lade Übungsdaten aus Firebase");
          return userDoc.data().exerciseResults || [];
        }
      } catch (error) {
        console.error("Fehler beim Laden der Übungsdaten aus Firebase:", error);
      }
    }
    
    // Fallback zu LocalStorage
    console.log("Lade Übungsdaten aus LocalStorage");
    const storedResults = localStorage.getItem('exercise_results');
    return storedResults ? JSON.parse(storedResults) : [];
  },
  
  /**
   * Ruft Fehlerstatistiken nach Tasten ab (zuerst aus Firebase, dann Fallback auf LocalStorage)
   * @returns {Promise<Object>} - Fehler nach Tasten
   */
  getMistakesStats: async function() {
    if (typeof window === 'undefined') return {};
    
    // Versuchen, Daten aus Firebase zu laden, wenn Benutzer angemeldet ist
    if (this.isUserLoggedIn()) {
      try {
        const userId = auth.currentUser.uid;
        const userStatsRef = doc(db, "users", userId);
        const userDoc = await getDoc(userStatsRef);
        
        if (userDoc.exists() && userDoc.data().mistakesStats) {
          console.log("Lade Fehlerstatistiken aus Firebase");
          return userDoc.data().mistakesStats || {};
        }
      } catch (error) {
        console.error("Fehler beim Laden der Fehlerstatistiken aus Firebase:", error);
      }
    }
    
    // Fallback zu LocalStorage
    console.log("Lade Fehlerstatistiken aus LocalStorage");
    const storedMistakes = localStorage.getItem('key_mistakes');
    return storedMistakes ? JSON.parse(storedMistakes) : {};
  },
  
  /**
   * Synchronisiert lokale Daten mit Firebase, wenn Benutzer angemeldet ist
   * @returns {Promise<boolean>} - Erfolg der Synchronisation
   */
  syncLocalDataWithFirebase: async function() {
    if (!this.isUserLoggedIn() || typeof window === 'undefined') return false;
    
    try {
      const userId = auth.currentUser.uid;
      const userStatsRef = doc(db, "users", userId);
      
      // Lokale Daten laden
      const localResults = localStorage.getItem('exercise_results');
      const localMistakes = localStorage.getItem('key_mistakes');
      
      if (!localResults && !localMistakes) {
        console.log("Keine lokalen Daten zum Synchronisieren vorhanden");
        return false;
      }
      
      // Bestehende Daten aus Firebase abrufen
      const userDoc = await getDoc(userStatsRef);
      let firebaseData = userDoc.exists() ? userDoc.data() : {};
      
      let firebaseResults = firebaseData.exerciseResults || [];
      let firebaseMistakes = firebaseData.mistakesStats || {};
      
      // Lokale Ergebnisse verarbeiten, wenn vorhanden
      if (localResults) {
        const parsedResults = JSON.parse(localResults);
        
        // Nur neue Ergebnisse hinzufügen (basierend auf Zeitstempel)
        const existingTimestamps = new Set(firebaseResults.map(r => r.timestamp));
        const newResults = parsedResults.filter(r => !existingTimestamps.has(r.timestamp));
        
        if (newResults.length > 0) {
          firebaseResults = [...firebaseResults, ...newResults];
        }
      }
      
      // Lokale Fehlerstatistiken verarbeiten, wenn vorhanden
      if (localMistakes) {
        const parsedMistakes = JSON.parse(localMistakes);
        
        // Fehlerstatistiken zusammenführen
        Object.keys(parsedMistakes).forEach(key => {
          if (!firebaseMistakes[key]) {
            firebaseMistakes[key] = parsedMistakes[key];
          } else {
            // Zähler aktualisieren
            firebaseMistakes[key].count += parsedMistakes[key].count || 0;
            
            // Falsch gedrückte Tasten zusammenführen
            if (parsedMistakes[key].wrongKeys) {
              Object.keys(parsedMistakes[key].wrongKeys).forEach(wrongKey => {
                if (!firebaseMistakes[key].wrongKeys[wrongKey]) {
                  firebaseMistakes[key].wrongKeys[wrongKey] = 0;
                }
                firebaseMistakes[key].wrongKeys[wrongKey] += parsedMistakes[key].wrongKeys[wrongKey];
              });
            }
          }
        });
      }
      
      // Aktualisierte Daten in Firebase speichern
      await setDoc(userStatsRef, {
        exerciseResults: firebaseResults,
        mistakesStats: firebaseMistakes,
        lastUpdated: Timestamp.now()
      }, { merge: true });
      
      console.log("Lokale Daten erfolgreich mit Firebase synchronisiert");
      return true;
    } catch (error) {
      console.error("Fehler bei der Synchronisation mit Firebase:", error);
      return false;
    }
  },
  
  /**
   * Berechnet Fortschrittsdaten für die Graphen
   * @returns {Promise<Array>} - Aufbereitete Daten für Fortschrittsdiagramme
   */
  getProgressChartData: async function() {
    const results = await this.getExerciseResults();
    if (!results.length) return [];
    
    // Gruppieren nach Datum
    const groupedByDate = {};
    
    results.forEach(result => {
      if (!groupedByDate[result.date]) {
        groupedByDate[result.date] = {
          totalCPM: 0,
          totalErrorRate: 0,
          totalDuration: 0,
          count: 0
        };
      }
      
      const dayStats = groupedByDate[result.date];
      dayStats.totalCPM += result.cpm || 0;
      dayStats.totalErrorRate += result.errorRate || 0;
      dayStats.totalDuration += result.duration || 0;
      dayStats.count++;
    });
    
    // Durchschnitte berechnen und in Array für Graph umwandeln
    return Object.keys(groupedByDate).map(date => {
      const stats = groupedByDate[date];
      return {
        date: date,
        cpm: Math.round(stats.totalCPM / stats.count),
        errorRate: +(stats.totalErrorRate / stats.count).toFixed(2),
        duration: Math.round(stats.totalDuration / 60) // In Minuten
      };
    });
  },
  
  /**
   * Gibt Zusammenfassungsstatistiken zurück
   * @returns {Promise<Object>} - Übungszusammenfassung
   */
  getSummaryStats: async function() {
    const results = await this.getExerciseResults();
    if (!results.length) {
      return {
        dataCompleteness: 0,
        avgCPM: 0,
        totalPoints: 0,
        avgErrorRate: 0,
        totalDuration: 0
      };
    }
    
    let totalCPM = 0;
    let totalErrorRate = 0;
    let totalPoints = 0;
    let totalDuration = 0;
    
    results.forEach(result => {
      totalCPM += result.cpm || 0;
      totalErrorRate += result.errorRate || 0;
      totalPoints += result.points || 0;
      totalDuration += result.duration || 0;
    });
    
    // Ermitteln, wie aussagekräftig die Daten sind (basierend auf Anzahl der Übungen)
    const dataCompleteness = Math.min(100, Math.round((results.length / 30) * 100));
    
    return {
      dataCompleteness: dataCompleteness,
      avgCPM: Math.round(totalCPM / results.length),
      totalPoints: totalPoints,
      avgErrorRate: +(totalErrorRate / results.length).toFixed(2),
      totalDuration: Math.round(totalDuration) // In Sekunden
    };
  },
  
  /**
   * Löscht alle gespeicherten Statistikdaten
   * @param {boolean} includeFirebase - Wenn true, werden auch Daten in Firebase gelöscht
   */
  clearAllStats: async function(includeFirebase = false) {
    if (typeof window === 'undefined') return;
    
    // Lokale Daten löschen
    localStorage.removeItem('exercise_results');
    localStorage.removeItem('key_mistakes');
    console.log("Alle lokalen Statistikdaten wurden gelöscht");
    
    // Firebase-Daten löschen, wenn gewünscht und angemeldet
    if (includeFirebase && this.isUserLoggedIn()) {
      try {
        const userId = auth.currentUser.uid;
        const userStatsRef = doc(db, "users", userId);
        
        await updateDoc(userStatsRef, {
          exerciseResults: [],
          mistakesStats: {},
          lastUpdated: Timestamp.now()
        });
        
        console.log("Alle Firebase-Statistikdaten wurden gelöscht");
      } catch (error) {
        console.error("Fehler beim Löschen der Firebase-Daten:", error);
      }
    }
  },
  
  /**
   * Aktiviert automatische Datensynchronisation bei Login/Logout
   * Diese Funktion sollte einmal beim App-Start aufgerufen werden
   */
  setupAutoSync: function() {
    if (typeof window === 'undefined') return;
    
    // Auth-Zustandsänderungen überwachen
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Bei Login lokale Daten mit Firebase synchronisieren
        console.log("Benutzer angemeldet, synchronisiere Daten...");
        await this.syncLocalDataWithFirebase();
      }
    });
  }
};
